/**
 * FieldProtectedRoute (Inventory Screens Step 4.2b) — the field-zone guard.
 *
 * FIELD ZONE ONLY. The app-wide ProtectedRoute (components/ProtectedRoute.jsx)
 * is untouched; this component mirrors its MSAL-account check and adds the
 * offline walls on top. Three states:
 *
 *   1. MSAL account present → normal online path (same effective behavior as
 *      ProtectedRoute): render children; the zone's Load fetches fresh
 *      identity/profile and re-persists the device snapshot.
 *   2. No account BUT a valid tmg-field snapshot exists → connectivity split
 *      (5.2c Part A, audit_field_auth_degradation_20260713.md):
 *        - navigator.onLine TRUE → the session died (recreated tab dropped
 *          the sessionStorage MSAL cache; the snapshot survived). Rendering
 *          the zone here would fake an offline shell on a live network
 *          (null callerEmail → refdata air-gap + stranded null-caller queue
 *          items). Render FieldReauthPrompt instead — sign-in is the only
 *          exit.
 *        - navigator.onLine FALSE → genuine OFFLINE access: role-gate off
 *          snapshot.identity.role (hasFieldAccess), render children with
 *          offline=true in FieldAuthContext. The snapshot only exists via a
 *          prior authenticated Load on this device, and it grants offline
 *          READ of already-cached data — never a write credential.
 *          Clears on device purge (fieldStore.purgeFieldDB).
 *   3. No account AND no snapshot (or snapshot fails the role gate) →
 *      LoginPage. A field zone can't bootstrap offline without a prior
 *      authenticated Load — correct posture.
 *
 * The snapshot read is async: while MSAL is mid-flight or IDB is resolving we
 * render the same "Loading..." shim as ProtectedRoute — never a LoginPage
 * flash before the snapshot has had its chance.
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import LoginPage from '../../../pages/LoginPage';
import FieldReauthPrompt from './FieldReauthPrompt';
import { getFieldSnapshot } from '../db/fieldStore';
import { hasFieldAccess } from '../../../shared/auth/fieldRoles';

const BOOT_STALL_MS = 8000;

// F-29: a bounded fallback for the two Provider-less Loading arms. Minimal by
// intent — a reload is the only action available to a rep whose sign-in or
// device read has not returned.
function FieldBootStalled({ reason }) {
  return (
    <div style={{ padding: '2rem 1.5rem', fontFamily: 'system-ui, sans-serif' }}>
      <p>Still waiting on {reason}.</p>
      <button type="button" onClick={() => window.location.reload()}>
        Reload
      </button>
    </div>
  );
}

const FieldAuthContext = createContext({ offline: false, networkDown: false, snapshot: null });

/**
 * { offline, networkDown, snapshot } — TWO INDEPENDENT SIGNALS, deliberately.
 *
 *   offline     — "I authenticated you from a device snapshot." Provenance of
 *                 IDENTITY, not connectivity. True on State 2b only, where
 *                 there is NO MSAL account and therefore NO callerEmail:
 *                 writes are impossible and the air-gap posture applies.
 *   networkDown — "navigator.onLine reports no network." Connectivity only,
 *                 read reactively. Orthogonal to `offline`: State 1b is
 *                 account-present PLUS networkDown, and there a callerEmail
 *                 DOES exist, so queued writes stay valid and drain on
 *                 reconnect.
 *
 * ⚠ Do not collapse them. One flag would either strand State 1b's writes or
 * hand State 2b a write credential it must not have. The split IS the fix
 * (F-29); redefining `offline` is not.
 */
export function useFieldAuth() {
  return useContext(FieldAuthContext);
}

export default function FieldProtectedRoute({ children }) {
  const { accounts, inProgress } = useMsal();
  const hasAccount = accounts.length > 0;

  // undefined = still resolving; null = resolved-absent; object = snapshot row
  const [snapshot, setSnapshot] = useState(undefined);

  useEffect(() => {
    if (hasAccount) return undefined; // online path never consults the snapshot
    let alive = true;
    getFieldSnapshot()
      .then((snap) => { if (alive) setSnapshot(snap || null); })
      .catch((err) => {
        console.error('FieldProtectedRoute: snapshot read failed', err);
        if (alive) setSnapshot(null);
      });
    return () => { alive = false; };
  }, [hasAccount]);

  // F-29: connectivity as a REACTIVE signal. navigator.onLine at the State 2
  // split below is a point-in-time read at a decision point, which is correct
  // there; FieldGuard needs a value that CHANGES when the radio does.
  // Listener/cleanup shape mirrors useFieldDrain.js:152-166.
  //
  // navigator.onLine === false is reliable; true only means an interface
  // exists, so this is a CONSERVATIVE test — it catches airplane mode and a
  // dead radio, and misses captive portals. Those are the cases useFieldLoad
  // and handleSelectSite already absorb with their failed-fetch fallbacks.
  const [networkDown, setNetworkDown] = useState(
    () => typeof navigator !== 'undefined' && !navigator.onLine,
  );
  useEffect(() => {
    function handleOnline() { setNetworkDown(false); }
    function handleOffline() { setNetworkDown(true); }
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // F-29 arms A and C: both Loading arms below render WITHOUT a Provider, so
  // offline they can sit indefinitely with no signal and no exit. A bounded
  // timer turns an unbounded spinner into something the rep can act on. The
  // arms and their conditions are UNCHANGED — this only bounds how long each
  // may show nothing. Keyed on `booting` so leaving and re-entering a Loading
  // arm restarts the clock instead of showing the stalled panel instantly.
  const booting = inProgress !== 'none' || (!hasAccount && snapshot === undefined);
  const [bootStalled, setBootStalled] = useState(false);
  useEffect(() => {
    if (!booting) {
      setBootStalled(false);
      return undefined;
    }
    const t = setTimeout(() => setBootStalled(true), BOOT_STALL_MS);
    return () => clearTimeout(t);
  }, [booting]);

  if (inProgress !== 'none') {
    return bootStalled ? <FieldBootStalled reason="sign-in" /> : <div>Loading...</div>;
  }

  // State 1 — online (MSAL account present)
  if (hasAccount) {
    return (
      <FieldAuthContext.Provider value={{ offline: false, networkDown, snapshot: null }}>
        {children}
      </FieldAuthContext.Provider>
    );
  }

  // No account: wait for the IDB read before deciding — no LoginPage flash.
  if (snapshot === undefined) {
    return bootStalled ? <FieldBootStalled reason="this device's saved data" /> : <div>Loading...</div>;
  }

  // State 2 — no account, snapshot passes the role gate: connectivity split
  // (5.2c Part A). Online means the session died, not a field outage — force
  // re-auth instead of rendering a degraded zone. navigator.onLine is read
  // at the decision point, each render.
  if (snapshot && hasFieldAccess(snapshot.identity?.role)) {
    if (navigator.onLine) {
      return <FieldReauthPrompt />;
    }
    return (
      <FieldAuthContext.Provider value={{ offline: true, networkDown, snapshot }}>
        {children}
      </FieldAuthContext.Provider>
    );
  }

  // State 3 — nothing to bootstrap from (or snapshot role fails the gate)
  return <LoginPage />;
}
